import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Import,
  Upload,
  Palette,
  HelpCircle,
  Plus,
  MoreVertical,
  Edit2,
  Trash2,
  Video,
} from 'lucide-react';
import type { VideoTemplate } from '../templates';

interface ProjectHeaderProps {
  projects: string[];
  currentProject: string | null;
  activeTemplate: VideoTemplate | null;
  showTemplatesPanel: boolean;
  isAuthenticated: boolean;
  onProjectChange: (project: string) => void;
  onCreateProject: () => void;
  onRenameProject: (projectName: string) => void;
  onDeleteProject: (projectName: string) => Promise<void>;
  onImport: () => void;
  onToggleTemplates?: () => void;
  getRootProps: () => Record<string, any>;
  getInputProps: () => Record<string, any>;
}

export const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  projects,
  currentProject,
  activeTemplate,
  showTemplatesPanel,
  isAuthenticated,
  onProjectChange,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onImport,
  onToggleTemplates,
  getRootProps,
  getInputProps,
}) => {
  return (
    <header className="flex h-12 items-center justify-between border-b border-border bg-[hsl(var(--panel-header))] px-4">
      {/* Left: Branding */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded bg-primary/15">
            <Video className="h-4 w-4 text-primary" />
          </div>
          <span className="text-sm font-semibold tracking-tight text-foreground">
            Hey Joe<span className="ml-1 text-primary">Plus</span>
          </span>
        </div>

        {/* Divider */}
        <div className="h-5 w-px bg-border" />

        {/* Project Selector */}
        <div className="flex items-center gap-1.5">
          {projects.length > 0 ? (
            <>
              <Select value={currentProject || ''} onValueChange={onProjectChange}>
                <SelectTrigger className="h-7 w-40 border-none bg-secondary/50 text-xs font-medium hover:bg-secondary">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {projects.map(project => (
                    <SelectItem 
                      key={project} 
                      value={project}
                      className="text-xs"
                    >
                      {project}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onCreateProject}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-secondary"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  New Project
                </TooltipContent>
              </Tooltip>

              {currentProject && currentProject !== 'default' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-secondary"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[140px] bg-popover border-border">
                    <DropdownMenuItem 
                      onClick={() => onRenameProject(currentProject)}
                      className="text-xs gap-2"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDeleteProject(currentProject)}
                      className="text-xs gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          ) : (
            <span className="text-xs text-muted-foreground">No projects</span>
          )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onImport}
          className="h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
        >
          <Import className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Import</span>
        </Button>

        <div {...getRootProps()}>
          <input {...getInputProps()} />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            <Upload className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Upload</span>
          </Button>
        </div>
        {onToggleTemplates && (
  
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleTemplates}
            className={`h-7 gap-1.5 px-2.5 text-xs transition-colors ${
              showTemplatesPanel 
                ? 'bg-primary/15 text-primary hover:bg-primary/20' 
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            }`}
          >
            <Palette className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Templates</span>
            {activeTemplate && (
              <span className="ml-1 rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                {activeTemplate.name}
              </span>
            )}
          </Button>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-secondary"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent 
            side="bottom" 
            align="end"
            className="max-w-[220px] bg-popover border-border text-xs"
          >
            <p className="text-muted-foreground">
              Import videos from your Hey Joe project or upload files directly.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </header>
  );
};
